<template>
  <div
    :class="DS_ACTION_BUTTONS_CLASSES.Root"
    data-cy="ds-action-buttons"
  >
    <div
      v-if="props.actions?.submit?.length"
      :class="DS_ACTION_BUTTONS_CLASSES.Group"
    >
      <DSButton
        v-for="(submitAction, submitActionIndex) in resolvedSubmitActions"
        :key="submitAction.id"
        v-bind="submitActionProps(submitAction, submitActionIndex)"
        @click="handleSubmitAction(submitAction)"
      >
        {{ submitAction.label }}
      </DSButton>
    </div>

    <div
      v-if="props.actions?.dismiss?.length"
      :class="DS_ACTION_BUTTONS_CLASSES.Group"
    >
      <DSButton
        v-for="dismissAction in resolvedDismissActions"
        :key="dismissAction.id"
        type="button"
        variant="secondary"
        :disabled="props.disabled"
        :prefix-icon="dismissAction.prefixIcon"
        :href="dismissAction.href"
        :target="dismissAction.target"
        :onclick="dismissAction.onclick"
        :data-cy="`ds-action-buttons__dismiss-button--${dismissAction.id}`"
        @click="handleDismissAction(dismissAction)"
      >
        {{ dismissAction.label }}
      </DSButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import type {
  DSDismissActionButtonDefinition,
  DSSubmitActionButtonDefinition,
} from "@/src/design_system/form_elements/action_buttons/types/actions";
import { DSActionButtonsProps } from "@/src/design_system/form_elements/action_buttons/types/props";
import { DSActionButtonsEmits } from "@/src/design_system/form_elements/action_buttons/types/emits";
import { DS_ACTION_BUTTONS_CLASSES } from "@/src/design_system/form_elements/action_buttons/cosntants/classes";
import { DS_BUTTON_VARIANT, DSButtonProps } from "@/src/design_system/button_and_controls/button/types/props";

const props = withDefaults(defineProps<DSActionButtonsProps>(), {
  disabled: false,
});

const emit = defineEmits<DSActionButtonsEmits>();

const resolvedSubmitActions = computed<DSSubmitActionButtonDefinition[]>(() => props.actions?.submit ?? []);
const resolvedDismissActions = computed<DSDismissActionButtonDefinition[]>(() => props.actions?.dismiss ?? []);

const handleDismissAction = (action: DSDismissActionButtonDefinition) => {
  if (!action.href) {
    emit("dismiss", action.id);
  }
};

const handleSubmitAction = (action: DSSubmitActionButtonDefinition) => {
  emit("submit", action.id);
};

const submitActionProps = (submitAction: DSSubmitActionButtonDefinition, submitActionIndex: number): DSButtonProps => {
  const isPrimaryAction = submitActionIndex === 0;
  const variant = isPrimaryAction ? DS_BUTTON_VARIANT.Primary : DS_BUTTON_VARIANT.Secondary;

  const baseProps = {
    variant,
    disabled: submitAction.disabled || props.disabled,
    prefixIcon: submitAction.prefixIcon,
    onclick: submitAction.onclick,
    "data-cy": `ds-action-buttons__submit-button--${submitAction.id}`,
  };

  return {
    ...baseProps,
    type: "submit",
    name: "action",
    value: submitAction.id,
    ...(props.formId ? { form: props.formId } : {}),
  };
};
</script>
